import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, AlertTitle, Box, Paper } from "@mui/material";
import bbox from "@turf/bbox";
import { ColDef, GetRowIdParams, GridOptions, ICellRendererParams, RowClassRules, RowClickedEvent, SelectionChangedEvent } from "ag-grid-community";
import { Feature, FeatureCollection } from "geojson";

import { LoadingButton } from "ui";

import { MapContext } from "fond/map/MapProvider";
import SourceAndLayers from "fond/map/SourceAndLayers";
import { getCurrentProjectData } from "fond/project";
import { replaceLayerFeatures, selectFeature } from "fond/project/redux";
import { buildLayerClassificationInputs, classifyParcelLayerInWorkerPool, DEFAULT_LAYER_CLASSIFICATION_CONFIG } from "fond/streetParcels";
import { isAnyLineString, isAnyPolygon, isPoint } from "fond/types/geojson";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { AgGrid, LinearProgressWithLabel, useStackedNavigationContext } from "fond/widgets";
import StreetParcelWorker from "fond/workers/streetParcels?worker";

import { OVERLAY_LAYER_CONFIG, OVERLAY_SOURCE_ID, OVERLAY_STYLES } from "./streetParcelOverlay";

import { Container, FOCUSED_ROW_CLASS } from "./IdentifyStreetParcels.styles";

const PARCEL_LAYER = "input_layers/parcel";
const STREET_LAYER = "input_layers/streets";
const DEMAND_LAYER = "input_layers/demand";

// Minimum probability for which parcels will be included in the list.
const INCLUSION_FLOOR = 0.6;

/**
 * Return a human-readable label for a parcel.
 *
 * We prefer:
 * - The street address (if present), or
 * - The APN (if present), or
 * - The parcel's numeric id (as a fallback).
 */
const parcelLabel = (properties: Record<string, unknown> | null, id: number): string => {
  const props = properties ?? {};
  const address = [props.StreetNumber, props.StreetName].filter(Boolean).join(" ").trim();
  if (address) return address;
  if (typeof props.PARCELAPN === "string" && props.PARCELAPN) return props.PARCELAPN;

  return `Parcel ${id}`;
};

type ConfidenceBand = "High" | "Medium" | "Low";

const HIGH_CONFIDENCE_THRESHOLD = 0.999;
const MEDIUM_CONFIDENCE_THRESHOLD = 0.95;

/* Return the confidence band for a given probability. */
const confidenceBand = (probability: number): ConfidenceBand => {
  if (probability > HIGH_CONFIDENCE_THRESHOLD) return "High";
  if (probability >= MEDIUM_CONFIDENCE_THRESHOLD) return "Medium";
  return "Low";
};

const BAND_COLOR: Record<ConfidenceBand, string> = { High: "success.main", Medium: "warning.main", Low: "grey.400" };

/** AgGrid cell renderer for a confidence range. */
const ConfidenceCell = ({ value }: ICellRendererParams<StreetParcelGridRow, number>): React.ReactNode => {
  if (value == null) return null;
  const band = confidenceBand(value);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%" }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BAND_COLOR[band], flexShrink: 0 }} />
      {band}
    </Box>
  );
};

type StreetParcelRow = {
  /* The index of this parcel. */
  id: number;

  /** The human-readable label for the parcel. */
  label: string;

  /** The calculated probability that this parcel is a street parcel. */
  probability: number;

  /** The identified parcel feature. */
  parcel: Feature;

  /** Demand features within the identified parcel. */
  demands: Feature[];
};

/** A parcel row augmented with view state for the grid. */
type StreetParcelGridRow = StreetParcelRow & {
  /** Whether this parcel is the one currently highlighted on the map. */
  isFocused: boolean;
};

/**
 * The state of the identification run.
 */
type Run = { status: "idle" } | { status: "running"; progress: number } | { status: "error" } | { status: "done"; rows: StreetParcelRow[] };

const GRID_OPTIONS: GridOptions<StreetParcelGridRow> = {
  defaultColDef: { sortable: false, filter: false, resizable: false, suppressHeaderMenuButton: true },
  rowSelection: { mode: "multiRow", checkboxes: true, headerCheckbox: true, enableClickSelection: false },
  getRowId: (params: GetRowIdParams<StreetParcelGridRow>) => String(params.data.id),
  sideBar: false,
  rowGroupPanelShow: "never",
  suppressContextMenu: true,
  suppressMovableColumns: true,
  suppressCellFocus: true,
  pagination: false,
};

// Compact grid dimensions
const GRID_HEADER_HEIGHT = 31;
const GRID_ROW_HEIGHT = 25;
const MAX_VISIBLE_ROWS = 12;

// focus the row corresponding to the parcel currently selected on the map (if any)
const ROW_CLASS_RULES: RowClassRules<StreetParcelGridRow> = {
  [FOCUSED_ROW_CLASS]: (params) => Boolean(params.data?.isFocused),
};

const layerFeatures = (projectData: ReturnType<typeof getCurrentProjectData>, layerId: string): Feature[] =>
  (projectData?.layers?.[layerId]?.features ?? []) as Feature[];

const StreetParcelInfo: React.FC = () => (
  <Alert severity="info" variant="compact" sx={{ mb: 2 }}>
    <AlertTitle>Use this tool to remove &ldquo;street parcels&rdquo;</AlertTitle>
    For the best underground network, remove parcels that intersect streets before running data preparation.
  </Alert>
);

/**
 * Data prep component that allows the user to identify and delete street parcels.
 */
const IdentifyStreetParcels: React.FC = () => {
  const dispatch = useAppDispatch();
  const { goBack } = useStackedNavigationContext();
  const { map } = useContext(MapContext);
  const projectData = useAppSelector((state) => getCurrentProjectData(state.project));
  const selectedFeature = useAppSelector((state) => state.project.selectedFeature);
  const focusedParcelId = selectedFeature?.layerId === PARCEL_LAYER ? selectedFeature.feature?.id : undefined;
  const [run, setRun] = useState<Run>({ status: "idle" });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // cancel an in-flight identification on unmount
  useEffect(() => () => abortRef.current?.abort(), []);

  const canRun = useMemo(
    () =>
      layerFeatures(projectData, PARCEL_LAYER).some(isAnyPolygon) &&
      layerFeatures(projectData, STREET_LAYER).some(isAnyLineString) &&
      layerFeatures(projectData, DEMAND_LAYER).some(isPoint),
    [projectData]
  );

  const columns = useMemo<ColDef<StreetParcelGridRow>[]>(
    () => [
      { headerName: "Parcel", field: "label", flex: 1 },
      { headerName: "Confidence", field: "probability", width: 130, cellRenderer: ConfidenceCell },
    ],
    []
  );

  const rows = useMemo<StreetParcelGridRow[]>(
    () => (run.status === "done" ? run.rows.map((row) => ({ ...row, isFocused: focusedParcelId != null && row.parcel.id === focusedParcelId })) : []),
    [run, focusedParcelId]
  );

  const identifiedParcels = useMemo<FeatureCollection>(
    () => ({
      type: "FeatureCollection",
      features: run.status === "done" ? run.rows.filter((row) => selectedIds.has(row.id)).map((row) => row.parcel) : [],
    }),
    [run, selectedIds]
  );
  const overlaySource = useMemo(() => ({ type: "geojson", data: identifiedParcels }), [identifiedParcels]);

  const handleIdentify = async () => {
    // Abort any existing run
    abortRef.current?.abort();

    const parcelFeatures = layerFeatures(projectData, PARCEL_LAYER);
    const streetFeatures = layerFeatures(projectData, STREET_LAYER);
    const demandFeatures = layerFeatures(projectData, DEMAND_LAYER);

    const { parcels, streets, demands, parcelFeatureById, demandFeatureById } = buildLayerClassificationInputs({
      parcelFeatures,
      streetFeatures,
      demandFeatures,
    });

    const abort = new AbortController();
    abortRef.current = abort;
    setRun({ status: "running", progress: 0 });

    try {
      const results = await classifyParcelLayerInWorkerPool(() => new StreetParcelWorker(), {
        parcels,
        streets,
        demands,
        config: { ...DEFAULT_LAYER_CLASSIFICATION_CONFIG, minProbability: INCLUSION_FLOOR },
        // a worker's progress message can arrive after another worker has already failed the run,
        // so only update the progress while the run is still going
        onProgress: (completed, total) =>
          setRun((prev) => (prev.status === "running" ? { status: "running", progress: total > 0 ? (completed / total) * 100 : 100 } : prev)),
        signal: abort.signal,
      });

      const resultRows = results
        // every parcel in FOND has an associated address point, however we don't keep track of this association.
        // the only guarantee we have is that the associated address point will be contained within the parcel's
        // geometry. unfortunately parcels can overlap, so this association is not completely reliable.
        //
        // here, we ultimately want to delete any street parcels (along with their associated address point).
        // if a candidate street parcel contains _more_ than one demand point, then (as explained above) we can't
        // determine the associated address. to avoid this ambiguity (<=> to avoid accidentally deleting the wrong
        // address), we restrict our attention to only those parcels that contain one or zero demand points.
        .filter((r) => r.probability != null && r.probability >= INCLUSION_FLOOR && r.demandIds.length <= 1)
        .flatMap((r): StreetParcelRow[] => {
          const id = Number(r.id);
          const parcel = parcelFeatureById.get(id);
          if (!parcel) return [];

          return [
            {
              id,
              label: parcelLabel(parcel.properties as Record<string, unknown> | null, id),
              probability: r.probability ?? 0,
              parcel,
              demands: r.demandIds.flatMap((demandId) => demandFeatureById.get(demandId) ?? []),
            },
          ];
        })
        .sort((a, b) => b.probability - a.probability);
      setSelectedIds(new Set(resultRows.map((r) => r.id)));
      setRun({ status: "done", rows: resultRows });
    } catch {
      if (!abort.signal.aborted) setRun({ status: "error" });
    }
  };

  const handleSelectionChanged = (event: SelectionChangedEvent<StreetParcelRow>) => {
    setSelectedIds(new Set(event.api.getSelectedRows().map((row) => row.id)));
  };

  const handleFocus = (row: StreetParcelRow) => {
    if (!map) return;
    const [minX, minY, maxX, maxY] = bbox(row.parcel);
    map.fitBounds(
      [
        [minX, minY],
        [maxX, maxY],
      ],
      // animation makes the process of reviewing parcels extremely slow, so disable them
      { padding: 40, animate: false }
    );
    dispatch(selectFeature({ ...row.parcel, source: PARCEL_LAYER }));
  };

  const handleRowClicked = (event: RowClickedEvent<StreetParcelRow>) => {
    if (event.data) handleFocus(event.data);
  };

  const deletableRows = useMemo(() => rows.filter((row) => selectedIds.has(row.id)), [rows, selectedIds]);

  const handleDelete = async () => {
    if (run.status !== "done" || deletableRows.length === 0) return;

    const deletedParcelIds = new Set(deletableRows.map((row) => row.parcel.id));
    const deletedDemandIds = new Set(deletableRows.flatMap((row) => row.demands.map((f) => f.id)));

    setIsDeleting(true);
    try {
      await dispatch(
        replaceLayerFeatures({
          [PARCEL_LAYER]: layerFeatures(projectData, PARCEL_LAYER).filter((f) => !deletedParcelIds.has(f.id)),
          [DEMAND_LAYER]: layerFeatures(projectData, DEMAND_LAYER).filter((f) => !deletedDemandIds.has(f.id)),
        })
      );
      goBack();
    } finally {
      setIsDeleting(false);
    }
  };

  const gridHeight = GRID_HEADER_HEIGHT + Math.min(rows.length, MAX_VISIBLE_ROWS) * GRID_ROW_HEIGHT + 2;

  return (
    <Container data-testid="identify-street-parcels" sx={{ px: 1 }}>
      {run.status === "done" && (
        <SourceAndLayers
          sourceId={OVERLAY_SOURCE_ID}
          source={overlaySource}
          styles={OVERLAY_STYLES}
          layers={[OVERLAY_LAYER_CONFIG]}
          isVisible={() => true}
        />
      )}

      {(run.status === "idle" || run.status === "error") && (
        <>
          <StreetParcelInfo />

          {run.status === "error" && (
            <Alert severity="error" variant="compact" sx={{ mb: 2 }} data-testid="identify-street-parcels-error">
              Something went wrong while identifying parcels. Please try again.
            </Alert>
          )}

          {!canRun && (
            <Alert severity="warning" variant="compact" sx={{ mb: 2 }}>
              The parcel, street and address layers must be present to review problematic parcels.
            </Alert>
          )}

          <LoadingButton
            fullWidth
            variant="contained"
            size="small"
            onClick={handleIdentify}
            disabled={!canRun}
            data-testid="identify-street-parcels-run"
          >
            Identify problematic parcels
          </LoadingButton>
        </>
      )}

      {run.status === "running" && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75 }}>
          <LinearProgressWithLabel color="primary" title="Identifying problematic parcels" value={run.progress} progressBarMarginRight={0} />
        </Paper>
      )}

      {run.status === "done" &&
        (rows.length === 0 ? (
          <Alert severity="success" variant="compact" data-testid="no-problematic-parcels">
            No problematic parcels were found.
          </Alert>
        ) : (
          <>
            <StreetParcelInfo />

            <AgGrid
              columnDefs={columns}
              rowData={rows}
              gridOptions={GRID_OPTIONS}
              rowClassRules={ROW_CLASS_RULES}
              onFirstDataRendered={(event) => event.api.selectAll()}
              onSelectionChanged={handleSelectionChanged}
              onRowClicked={handleRowClicked}
              containerProps={{ height: gridHeight }}
              variant="outlined"
              size="compact"
            />

            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="error"
                size="small"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={deletableRows.length === 0}
                data-testid="delete-selected-parcels"
              >
                Delete {deletableRows.length} selected {deletableRows.length === 1 ? "parcel" : "parcels"}
              </LoadingButton>
            </Box>
          </>
        ))}
    </Container>
  );
};

export default IdentifyStreetParcels;
